import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Step,
  StepButton,
  Stepper,
  TextField,
} from "@mui/material";
import * as React from "react";
import BatteryTypeSelect from "../selects/battery_type_select";
import MeterTypeSelect from "../selects/meter_type_select";
import DeviceClassSelect from "../selects/device_class_select";
import USBPortSelect from "../selects/usb_port_select";
import { useSnackbar } from "notistack";
import { processValue } from "../../../scripts/utils";
import HelpedSelect from "../../general/helped_select";
import { Protocol } from "../../../model/device";

const devTemplate = { device_type: "", name: "", device_class: "" };
//const meterTemplate = { "device_type": "Gavazzi", "device_id": "", "name": "", "sampling_time": 1, "enabled": true, "address": "", "protocol": "RS485", "address": "/dev/ttyUSB0", "device_class": "meter", "mode": 2, "direction": 1 }
//const batteryTemplate = { "device_type": "Victron", "device_id": "", "name": "", "sampling_time": 1, "enabled": true, "address": "", "protocol": "TCP", "port": 502, "device_class": "battery", "capacity": 0, "charge_rate": 0, "mppt_tracking_mode": 0, "battery_tracking_mode": 0 }

const steps = ["Select device type", "Select protocol"];

function TypeSelect({ device, handleDevChange }) {
  if (device.device_class === "battery") {
    return (
      <Grid
        md={6}
        xs={12}
        item
        width={"80%"}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        px={2}
        py={2}
      >
        <BatteryTypeSelect
          value={device.device_type}
          onChange={(e) => {
            handleDevChange("device_type", e.target.value, true);
          }}
        />
      </Grid>
    );
  } else if (device.device_class === "meter") {
    return (
      <Grid
        md={6}
        xs={12}
        item
        width={"80%"}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        px={2}
        py={2}
      >
        <MeterTypeSelect
          value={device.device_type}
          onChange={(e) => {
            handleDevChange("device_type", e.target.value, true);
          }}
        />
      </Grid>
    );
  }
}

function isInternetProtocol(protocol) {
  return protocol === Protocol.TCP || protocol === Protocol.TCP_RTU;
}

export function AddDiscoveredStepper({ device, setDevice, open, setOpen, handleAddDevice }) {
  //const [device, setDevice] = React.useState(dev_config)
  const [activeStep, setActiveStep] = React.useState(0);
  const [completed, setCompleted] = React.useState({});
  const { enqueueSnackbar } = useSnackbar();

  const totalSteps = () => {
    return steps.length;
  };

  // const completedSteps = () => {
  //     return Object.keys(completed).length;
  // };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const allStepsCompleted = () => {
    //return completedSteps() === totalSteps();
    return true;
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  function handleClose() {
    setOpen(false);
    setActiveStep(0);
    setDevice({});
  }

  function handleClassChange(value) {
    handleDevChange("device_class", value, true);
  }

  function handleProtocolChange(value) {
    setDevice((prev) => {
      return { ...prev, address: "" };
    });
    handleDevChange("protocol", value, true);
  }

  function handleDevChange(field, value, isString = false) {
    const processed = processValue(value, isString);
    setDevice((prev) => {
      const updated = { ...prev };
      updated[field] = processed;
      return updated;
    });
  }

  function validateDevice(device) {
    if (device.name === "") {
      return { type: "error", message: "Please fill in a name!", returnStep: 0 };
    } else if (device.protocol === "TCP") {
      if (device.device_id === "") {
        return { type: "error", message: "Please fill in a MAC address!", returnStep: 1 };
      } else if (device.address === "") {
        return { type: "error", message: "Please fill in an IP address!", returnStep: 1 };
      } else if (device.port === "") {
        return { type: "error", message: "Please fill in a port!", returnStep: 1 };
      }
    } else if (device.protocol === "RS485") {
      if (device.address === "") {
        return { type: "error", message: "Please select an USB port!", returnStep: 1 };
      }
    }
    return { type: "success", message: "Device added!" };
  }

  function handleAdd() {
    setActiveStep(0);
    const dev = { ...device };
    const validation = validateDevice(dev);
    if (validation.type === "success") {
      try {
        setDevice(devTemplate);
        enqueueSnackbar(validation.message, { variant: "success" });
        return handleAddDevice(dev);
      } catch (e) {
        enqueueSnackbar("Device add failed", { variant: "error" });
      }
    } else {
      setActiveStep(validation.returnStep); // go to the step with wrongfilled data
      enqueueSnackbar(validation.message, { variant: "error" });
    }
  }

  function renderLabel(index, label) {
    if (activeStep <= index) {
      return label;
    } else if (index === 0) {
      return device.device_class
        ? device.device_class.toUpperCase() + ": " + device.device_type
        : "BATTERY: " + device.device_type;
    } else if (index === 1) {
      return device.protocol;
    }
  }

  if (Object.values(device).length === 0) return;
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      fullWidth
      maxWidth={"md"}
      classes={{ paper: { minHeight: "80vh", maxHeight: "80vh" } }}
    >
      <DialogTitle id="add-dialog-title">Add discovered device</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep}>
          {steps.map((label, index) => (
            <Step key={label} completed={completed[index]}>
              <StepButton color="inherit" onClick={handleStep(index)}>
                {renderLabel(index, label)}
              </StepButton>
            </Step>
          ))}
        </Stepper>
        <Grid mt={3} container justifyContent="start">
          {activeStep === 0 && (
            <>
              <Grid
                md={6}
                xs={12}
                item
                width={"80%"}
                sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                px={2}
                py={2}
              >
                <DeviceClassSelect value={device.device_class} onChange={(e) => handleClassChange(e.target.value)} />
              </Grid>
              <TypeSelect device={device} handleDevChange={handleDevChange} />
              <Grid
                md={6}
                xs={12}
                item
                width={"80%"}
                sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                px={2}
                py={2}
              >
                <TextField
                  label={"Device name"}
                  fullWidth
                  value={device.name}
                  onChange={(e) => handleDevChange("name", e.target.value, true)}
                />
              </Grid>
            </>
          )}
          {activeStep === 1 && (
            <>
              <Grid
                md={6}
                xs={12}
                item
                width={"80%"}
                sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                px={2}
                py={2}
              >
                <HelpedSelect
                  options={Protocol}
                  label={"Protocol"}
                  key="protocol"
                  info="Select the communication protocol between elion box and device"
                  selected={device.protocol}
                  onChange={(e) => {
                    handleProtocolChange(e.target.value);
                  }}
                />
              </Grid>
              {isInternetProtocol(device.protocol) && (
                <Grid
                  md={6}
                  xs={12}
                  item
                  width={"80%"}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  px={2}
                  py={2}
                >
                  <TextField
                    label={"MAC address"}
                    fullWidth
                    value={device.device_id}
                    onChange={(e) => handleDevChange("device_id", e.target.value, true)}
                  />
                </Grid>
              )}
              {isInternetProtocol(device.protocol) ? (
                <Grid
                  md={6}
                  xs={12}
                  item
                  width={"80%"}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  px={2}
                  py={2}
                >
                  <TextField
                    label={(device.protocol === Protocol.TCP ? "IP" : "Device") + " address"}
                    fullWidth
                    value={device.address}
                    onChange={(e) => handleDevChange("address", e.target.value, true)}
                  />
                </Grid>
              ) : (
                <Grid
                  md={6}
                  xs={12}
                  item
                  width={"80%"}
                  sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                  px={2}
                  py={2}
                >
                  <USBPortSelect
                    value={device.address}
                    onChange={(e) => {
                      handleDevChange("address", e.target.value, true);
                    }}
                  />
                </Grid>
              )}
              {isInternetProtocol(device.protocol) && (
                <Grid
                  md={6}
                  xs={12}
                  item
                  width={"80%"}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  px={2}
                  py={2}
                >
                  <TextField
                    label={"Port"}
                    fullWidth
                    value={device.port}
                    onChange={(e) => handleDevChange("port", e.target.value, false)}
                  />
                </Grid>
              )}
              <Grid
                md={6}
                xs={12}
                item
                width={"80%"}
                sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                px={2}
                py={2}
              >
                <TextField
                  fullWidth
                  label={"Modbus slave ID"}
                  key="slave"
                  info="Select the modbus slave/unit ID"
                  value={device.slave}
                  onChange={(e) => {
                    handleDevChange("slave", e.target.value, false);
                  }}
                />
              </Grid>
            </>
          )}
          {/* {activeStep === 2 && <>
                    {(device.device_class === "battery" || !device.device_class) &&
                        <>
                            <Grid md={6} xs={12} item width={"80%"} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }} px={2} py={2}>
                                <BatteryTrackingSelect batteryType={device.device_type} value={device.battery_tracking_mode ? device.battery_tracking_mode : 0} onChange={(e) => handleDevChange("battery_tracking_mode", e.target.value, false)} />
                            </Grid>
                            <Grid md={6} xs={12} item width={"80%"} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }} px={2} py={2}>
                                <MPPTTrackingSelect disabled={true} value={100} onChange={(e) => handleDevChange("mppt_tracking_mode", e.target.value, false)} />
                            </Grid>
                        </>
                    }
                    {device.device_class === "meter" &&
                        <>
                            <Grid md={6} xs={12} item width={"80%"} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }} px={2} py={2}>
                                <MeteringModeSelect value={device.mode} onChange={(e) => handleDevChange("mode", e.target.value, false)} />
                            </Grid>
                            <Grid md={6} xs={12} item width={"80%"} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }} px={2} py={2}>
                                <MeteringDirectionSelect value={device.direction} onChange={(e) => handleDevChange("direction", e.target.value, false)} />
                            </Grid>
                        </>
                    }
                    <Grid md={6} xs={12} item width={"80%"} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }} px={2} py={2}>
                        <TextField label={"Sampling time"} fullWidth value={device.sampling_time ? device.sampling_time : Number(1)} onChange={(e) => handleDevChange("sampling_time", e.target.value, false)} />
                    </Grid>
                    <Grid md={6} xs={12} item width={"80%"} />
                </>} */}
        </Grid>
        <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
          {activeStep === 0 ? (
            <Button onClick={handleClose} sx={{ mr: 1, color: "red" }}>
              Cancel
            </Button>
          ) : (
            <Button color="inherit" disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
              Back
            </Button>
          )}
          <Box sx={{ flex: "1 1 auto" }} />
          {!isLastStep() ? (
            <Button onClick={handleNext} sx={{ mr: 1 }}>
              Next
            </Button>
          ) : (
            <Button
              disabled={!allStepsCompleted()}
              onClick={() => {
                handleAdd();
              }}
            >
              Add device
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
